import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { Loading, MockBadge } from "@/components/Loading";
import { AdminPrintify } from "@/components/admin/AdminPrintify";
import { AdminYouTube } from "@/components/admin/AdminYouTube";

export const Stat = ({ label, value, note, testid }) => (
  <div className="paper-card p-5" data-testid={testid}>
    <p className="text-sm font-semibold uppercase tracking-wide text-[#8C7A6B]">{label}</p>
    <p className="mt-2 font-serif text-3xl font-bold text-[#2C1E16]">{value}</p>
    {note && <p className="mt-1 text-sm text-[#5C4A3E]">{note}</p>}
  </div>
);

const SendNow = ({ path, label, text, testid }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      data.status === "sent" ? toast.success(`${label} emailed to you`) : toast.error(data.error || "Could not send the email");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-lg"><b>{label}</b> - {text}</p>
      <button className="btn-outline shrink-0" onClick={send} disabled={busy} data-testid={`${testid}-send-now-button`}>{busy ? "Sending..." : `Email me the ${label.toLowerCase()} now`}</button>
    </div>
  );
};

const AlertTest = () => {
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState("");
  useEffect(() => { api.get("/admin/order-alert").then(({ data }) => setTo(data.emails.join(", "))); }, []);
  const call = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const save = () => call(async () => {
    const { data } = await api.put("/admin/order-alert", { emails: to.split(",").map((e) => e.trim()).filter(Boolean) });
    setTo(data.emails.join(", ")); toast.success("Alert address saved");
  });
  const test = () => call(async () => {
    const { data } = await api.post("/admin/order-alert/test");
    data.status === "sent" ? toast.success(`Test alert sent to ${data.to.join(", ")}`) : toast.error(data.error);
  });
  return (
    <div className="space-y-2" data-testid="order-alert-card">
      <p className="text-lg"><b>New order alerts</b> - a short email every time an order is placed. Separate several addresses with commas.</p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input className="field !min-h-0 !py-2 !text-base sm:flex-1" value={to} onChange={(e) => setTo(e.target.value)} placeholder="iq.ai.solutions@gmail.com" data-testid="order-alert-email-input" />
        <button className="btn-outline shrink-0" onClick={save} disabled={busy} data-testid="order-alert-save-button">Save</button>
        <button className="btn-outline shrink-0" onClick={test} disabled={busy} data-testid="order-alert-test-button">{busy ? "Sending..." : "Send a test alert"}</button>
      </div>
    </div>
  );
};

const SummaryButton = () => (
  <div className="paper-card space-y-4 p-5" data-testid="daily-summary-card">
    <AlertTest />
    <SendNow path="/admin/activity-report/send-now" label="Activity report" text="visits, new accounts, uploads, YouTube posts and print orders, every morning at 6:00 AM Eastern." testid="activity-report" />
    <SendNow path="/admin/weekly-trends/send-now" label="Weekly trends" text="this week compared with last week, every Monday at 6:00 AM Eastern." testid="weekly-trends" />
    <SendNow path="/admin/daily-summary/send-now" label="Daily summary" text="orders and anything waiting for you, every morning at 8:00 AM Eastern." testid="daily-summary" />
  </div>
);

export const AdminOverview = () => {
  const [s, setS] = useState(null);
  const [i, setI] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data));
    api.get("/admin/integrations").then(({ data }) => setI(data));
  }, []);
  if (!s || !i) return <Loading />;
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="flex flex-wrap gap-3" data-testid="integration-modes">
        {i.youtube.mode === "mock" ? <MockBadge testid="youtube-mode-badge">YouTube adapter</MockBadge> : <span className="badge-mock !border-[#2E6930] !bg-[#E8F3E8] !text-[#2E6930]">YouTube LIVE</span>}
        {i.printful.mode === "mock" ? <MockBadge testid="printful-mode-badge">Printify adapter</MockBadge> : <span className="badge-mock !border-[#2E6930] !bg-[#E8F3E8] !text-[#2E6930]" data-testid="printful-mode-badge">Printify {i.printful.mode === "live" ? "LIVE" : "DRAFT"}</span>}
        <span className="badge-mock !border-[#5C4A3E] !bg-white !text-[#5C4A3E]">Stripe {i.stripe.mode} mode</span>
        {i.require_admin_approval === true && <span className="badge-mock !border-[#A83232] !bg-white !text-[#A83232]">Admin approval required</span>}
      </div>
      <AdminYouTube />
      <AdminPrintify />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Revenue" value={money(s.revenue)} note={`${s.paid_orders} paid · ${s.refunded_orders} refunded`} testid="stat-revenue" />
        <Stat label="Payment fees" value={money(s.payment_fees)} note={s.fees_estimated_count ? `${s.fees_estimated_count} estimated (2.9% + $0.30)` : "Actual Stripe fees"} testid="stat-fees" />
        <Stat label="Shipping we pay" value={money(s.shipping_costs + s.shipping_quoted_unfulfilled)} note={`Customers pay $0. ${money(s.shipping_costs)} actual Printify + ${money(s.shipping_quoted_unfulfilled)} quoted for unsent orders`} testid="stat-shipping" />
        <Stat label="Fulfillment costs" value={money(s.fulfillment_costs)} note={s.fulfillment_simulated_count ? `${s.fulfillment_simulated_count} simulated or draft (not printed)` : "Actual Printify costs"} testid="stat-fulfillment" />
        <Stat label="Commissions" value={money(s.commissions)} testid="stat-commissions" />
        <Stat label="Contribution margin" value={money(s.contribution_margin)} note="Revenue − fees − fulfillment − commissions" testid="stat-margin" />
        <Stat label="Awaiting approval" value={s.awaiting_approval} testid="stat-awaiting" />
        <Stat label="Failed jobs" value={s.failed_jobs} testid="stat-failed-jobs" />
        <Stat label="Customers" value={s.customers} note={`${s.open_removals} open removal requests`} testid="stat-customers" />
      </div>
      <SummaryButton />
      <div className="paper-card p-5">
        <p className="font-semibold">Orders by status</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(s.by_status).map(([k, v]) => <span key={k} className="rounded-full bg-[#F2EBE1] px-3 py-1 text-sm font-semibold" data-testid={`status-count-${k}`}>{k}: {v}</span>)}
        </div>
      </div>
    </div>
  );
};
